/**
 * Pure helpers for the gang screens: permission labels, rank checks the UI uses to decide which
 * buttons to OFFER (the server re-checks everything), tier progress, audit sentences, cosmetics.
 * No DOM, no engine — unit-tested in gangFormat.test.ts.
 */
import { GANG_COLORS, GANG_EMBLEMS, GANG_NAME_RE, GANG_PERMISSIONS, GANG_REP, GANG_REP_DAILY_CAP, GANG_TAG_RE, GANG_TIERS, type GangPermission, type GangTier } from '../../shared/gangs';
import { LOCKUPS } from '../../shared/economy';
import { getItemDef } from '../../shared/items';
import type { GangAuditView, GangMemberView, GangRoleView, GangView } from '../../shared/messages/gangs';

// ----------------------------------------------------------------------------- numbers & dates

export function money(n: number): string {
  return `£${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export function colorCss(n: number): string {
  return `#${(n & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** "just now", "5 min ago", "3 h ago", "2 d ago", else "8 Oct" (server timestamps are epoch ms). */
export function timeAgo(ts: number, now: number): string {
  const s = Math.max(0, (now - ts) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  if (s < 86_400 * 14) return `${Math.round(s / 86_400)} d ago`;
  const d = new Date(ts);
  return `${d.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]}`;
}

/** Minutes left until a server-time deadline (null when the clocks make no sense). */
export function minutesLeft(expiresAt: number, serverNow: number): number | null {
  const ms = expiresAt - serverNow;
  if (!Number.isFinite(ms) || ms > 48 * 3_600_000) return null;
  return Math.max(0, Math.ceil(ms / 60_000));
}

// ----------------------------------------------------------------------------- permissions & ranks

export const PERMISSION_LABELS: Record<GangPermission, { label: string; help: string }> = {
  invite: { label: 'Invite', help: 'Invite people into the gang' },
  kick: { label: 'Kick', help: 'Throw out members ranked below you' },
  promote: { label: 'Promote', help: 'Change the role of members ranked below you' },
  'treasury.deposit': { label: 'Deposit', help: 'Put money in the treasury' },
  'treasury.withdraw': { label: 'Withdraw', help: 'Take money out of the treasury (within the role limit)' },
  'property.manage': { label: 'Property', help: 'Order supplies and pay the bills of gang premises (treasury); run a mate’s own premises they opened to the gang' },
  'property.buy': { label: 'Buy premises', help: 'Buy premises and fit operations for the gang (treasury); accept premises moved in or bought out' },
  'property.collect': { label: 'Collect', help: 'Box up stock, sell the takings to the wholesaler, use the store of gang premises' },
  'property.upgrade': { label: 'Improve', help: 'Buy improvements for gang premises (treasury)' },
  'hideout.furnish': { label: 'Hideout', help: 'Furnish the gang’s hideout and buy its livery (treasury; looks only)' },
  'jobs.start': { label: 'Start jobs', help: 'Start gang jobs such as the shipment run' },
  'inventory.manage': { label: 'Stash', help: 'Use the shared stash at the hideout and take stock out of gang premises' },
  'roles.edit': { label: 'Roles', help: 'Create and edit roles below your rank; change gang colours' },
  'audit.view': { label: 'Audit', help: 'Read the gang ledger' },
};

export function permissionLabel(p: string): string {
  return PERMISSION_LABELS[p as GangPermission]?.label ?? p;
}

type YouLike = Pick<GangView['you'], 'permissions' | 'boss' | 'rank'>;

/** The server already expands the boss's implicit permissions; `boss` is kept as a belt-and-braces. */
export function hasPerm(you: YouLike | null | undefined, p: GangPermission): boolean {
  return !!you && (you.boss || you.permissions.includes(p));
}

export const outranks = (myRank: number, theirRank: number): boolean => myRank > theirRank;

export function roleOf(gang: Pick<GangView, 'roles'>, id: number): GangRoleView | undefined {
  return gang.roles.find((r) => r.id === id);
}

export function sortedRoles(roles: readonly GangRoleView[]): GangRoleView[] {
  return [...roles].sort((a, b) => b.rank - a.rank);
}

/** Roles you may hand out (invite / promote / demote): strictly below your rank, never the boss role. */
export function assignableRoles(gang: Pick<GangView, 'roles' | 'you'>): GangRoleView[] {
  return sortedRoles(gang.roles).filter((r) => !r.boss && r.rank < gang.you.rank);
}

/** Roles you may edit or delete: strictly below your rank, never the boss role. */
export function editableRoles(gang: Pick<GangView, 'roles' | 'you'>): GangRoleView[] {
  return assignableRoles(gang);
}

export function memberRank(gang: Pick<GangView, 'roles'>, m: GangMemberView): number {
  return roleOf(gang, m.role)?.rank ?? 0;
}

export interface MemberActions {
  setRole: boolean;
  kick: boolean;
  transfer: boolean;
}

/** Which per-member buttons to offer. */
export function memberActions(gang: GangView, m: GangMemberView, myCid: number | null): MemberActions {
  const isMe = myCid !== null && m.cid === myCid;
  const below = outranks(gang.you.rank, memberRank(gang, m));
  return {
    setRole: !isMe && below && hasPerm(gang.you, 'promote') && assignableRoles(gang).length > 0,
    kick: !isMe && below && hasPerm(gang.you, 'kick'),
    transfer: !isMe && gang.you.boss,
  };
}

/** Can the permission be switched ON in the role editor? (You can't hand out what you don't hold.) */
export function canGrantPermission(you: YouLike, p: GangPermission): boolean {
  return hasPerm(you, p);
}

export function describeWithdrawLimit(limit: number | null): string {
  if (limit === null) return 'unlimited';
  if (limit === 0) return 'none';
  return `${money(limit)}/day`;
}

export function describeWithdrawable(you: GangView['you']): string {
  if (!hasPerm(you, 'treasury.withdraw')) return 'your role can’t withdraw';
  return you.withdrawable === null ? 'unlimited' : money(you.withdrawable);
}

export function permissionList(perms: readonly GangPermission[]): GangPermission[] {
  return GANG_PERMISSIONS.filter((p) => perms.includes(p));
}

// ----------------------------------------------------------------------------- progression

export interface TierProgress {
  tier: number;
  /** 0..1 within the current tier (1 at the top tier). */
  fraction: number;
  /** Reputation still needed for the next tier (0 at the top). */
  remaining: number;
  label: string;
}

export function tierProgress(reputation: number, tier: { tier: number; nextRep: number | null }): TierProgress {
  const from = GANG_TIERS.find((t) => t.tier === tier.tier)?.rep ?? 0;
  if (tier.nextRep === null) return { tier: tier.tier, fraction: 1, remaining: 0, label: `${reputation} reputation · top tier` };
  const span = Math.max(1, tier.nextRep - from);
  const fraction = Math.max(0, Math.min(1, (reputation - from) / span));
  const remaining = Math.max(0, tier.nextRep - reputation);
  return { tier: tier.tier, fraction, remaining, label: `${reputation} / ${tier.nextRep} reputation` };
}

export const TIER_NAMES = ['Street crew', 'Local firm', 'Borough name', 'Kingpins'] as const;
export const tierName = (tier: number): string => TIER_NAMES[tier] ?? `Tier ${tier}`;

/** What the next tier opens, in a line ("room for 10 members, a stash of 24, more colours"). */
export function tierUnlocks(tier: number): string | null {
  const t = GANG_TIERS.find((x) => x.tier === tier);
  const prev = GANG_TIERS.find((x) => x.tier === tier - 1);
  if (!t || !prev) return null;
  const parts = [`room for ${t.maxMembers} members`, `a stash of ${t.stashUnits} units`];
  if (t.lockupTier > prev.lockupTier) parts.push(`${t.lockupTier} premises at once`);
  parts.push('more colours and emblems');
  return parts.join(', ');
}

/** What a gang is for, in two sentences (the founding sheet and the book). */
export const GANG_PITCH = 'A gang is a firm that lasts: a shared treasury and stash, ranks, your colours on your motors, premises the gang owns together, and turf — the districts your firm runs. It grows with reputation from the work you do together.';

/** How a gang earns reputation, for the ledger and the book (the sources in shared/gangs.ts GANG_REP). */
export const REPUTATION_HOW = `Reputation comes from working together: Night Freight with two or more of you (${GANG_REP.freightBase} + ${GANG_REP.freightPerCrate} a crate), selling the gang business's takings to the wholesaler, coming to run a district (${GANG_REP.turfLead}) and winning a contest for one (${GANG_REP.turfContest}). At most ${GANG_REP_DAILY_CAP} a day.`;

export interface GangNextFacts {
  /** Crew mates in the city now, you included (0 = no crew). */
  crewOnline: number;
  /** Premises the gang holds and may hold (null = not heard yet). */
  premises: { held: number; cap: number } | null;
  /** Districts the gang runs in this shard (null = not heard yet). */
  led: number | null;
}

export interface GangNextStep {
  id: 'recruit' | 'crew' | 'freight' | 'treasury' | 'business' | 'turf';
  title: string;
  text: string;
  /** The ledger tab that does it. */
  tab: 'members' | 'treasury' | 'business' | 'turf' | 'crew';
}

/**
 * "What your gang can do next": at most three steps, the most useful first, from the gang view and what the
 * store knows. Pure — the ledger's Overview and the book's GANG page both show it.
 */
export function gangNextSteps(g: GangView, f: GangNextFacts): GangNextStep[] {
  const out: GangNextStep[] = [];
  const online = g.members.filter((m) => m.online).length;
  const canInvite = hasPerm(g.you, 'invite');
  if (g.members.length < 2) {
    out.push({
      id: 'recruit',
      title: 'Bring your mates in',
      text: canInvite ? 'A gang of one earns no reputation: invite your mates by name on the Members tab.' : 'A gang of one earns no reputation: ask your boss to invite your mates.',
      tab: 'members',
    });
  }
  if (g.members.length >= 2 && online >= 2 && f.crewOnline < 2) {
    out.push({ id: 'crew', title: 'Crew up for Night Freight', text: `${online} of you are about. Make a crew on the crew sheet and run Night Freight from the pier together: it pays best and earns the gang reputation.`, tab: 'crew' });
  } else if (f.crewOnline >= 2) {
    out.push({ id: 'freight', title: 'Run Night Freight', text: 'Your crew is about: meet at Bonded Warehouse No. 3, one of you driving a car with a boot. Two or more of the gang on the run earns reputation.', tab: 'crew' });
  }
  if (f.premises && f.premises.held < f.premises.cap) {
    const cheapest = Math.min(...LOCKUPS.map((l) => l.price));
    const short = g.treasury < cheapest;
    out.push({
      id: 'business',
      title: f.premises.held === 0 ? 'Set up a gang business' : 'Room for another business',
      text: short
        ? `Premises are paid from the treasury (from ${money(cheapest)}, plus fitting out). Everyone can put money in on the Treasury tab.`
        : 'The treasury can buy premises: stand at a lockup door and buy it for the gang. Its goods sell to the gang’s wholesaler, into the treasury, and earn reputation.',
      tab: short ? 'treasury' : 'business',
    });
  }
  if (f.led === 0) {
    out.push({ id: 'turf', title: 'Run a district', text: 'Sales, freight drops and jobs in one district build your say there; the firm with the most runs it and its buyers pay you 3 % more. The Turf tab has the standings.', tab: 'turf' });
  }
  if (out.length === 0 && g.tier.nextRep !== null) {
    out.push({ id: 'freight', title: `${g.tier.nextRep - g.reputation} reputation to the next tier`, text: `It opens ${tierUnlocks(g.tier.tier + 1) ?? 'more'}. Night Freight together is the quickest way there.`, tab: 'crew' });
  }
  return out.slice(0, 3);
}

// ----------------------------------------------------------------------------- cosmetics

export const EMBLEM_GLYPH: Record<(typeof GANG_EMBLEMS)[number], string> = {
  skull: '☠',
  crown: '♛',
  anchor: '⚓',
  dagger: '†',
  rose: '❀',
  star: '★',
  crow: '♞',
  wheel: '☸',
  key: '⚷',
  wave: '≈',
  eye: '◉',
  bolt: '⚡',
};

export const emblemGlyph = (emblem: string): string => (EMBLEM_GLYPH as Record<string, string>)[emblem] ?? '✦';

export const unlockedColors = (tier: Pick<GangTier, 'colors'>): readonly number[] => GANG_COLORS.slice(0, tier.colors);
export const unlockedEmblems = (tier: Pick<GangTier, 'emblems'>): readonly (typeof GANG_EMBLEMS)[number][] => GANG_EMBLEMS.slice(0, tier.emblems);

/** Hints only (the server decides): null = looks fine. */
export function nameHint(name: string): string | null {
  const n = name.trim().replace(/\s+/g, ' ');
  if (n.length === 0) return null;
  if (!GANG_NAME_RE.test(n)) return 'Names are 3–24 letters, digits, spaces, hyphens or apostrophes.';
  return null;
}

export function tagHint(tag: string): string | null {
  const t = tag.trim();
  if (t.length === 0) return null;
  if (!GANG_TAG_RE.test(t)) return 'Tags are 2–4 letters or digits.';
  return null;
}

// ----------------------------------------------------------------------------- audit trail

const pounds = (v: unknown): string => (typeof v === 'number' ? money(v) : '£?');
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' && v.length > 0 ? v : fallback);

/** One readable sentence for an audit row, e.g. "Ada took £500 from the treasury". */
export function auditSentence(e: Pick<GangAuditView, 'action' | 'actorName' | 'targetName' | 'data' | 'actor'>): string {
  const d = e.data ?? {};
  const who = e.actorName ?? (e.actor === null ? 'The gang' : 'A former member');
  const target = e.targetName ?? str(d.targetName, 'someone');
  switch (e.action) {
    case 'gang.create':
      return `${who} founded ${str(d.name, 'the gang')}${d.tag ? ` [${str(d.tag)}]` : ''}`;
    case 'gang.invite':
      return `${who} invited ${target} as ${str(d.role, 'a member')}`;
    case 'gang.invite.cancel':
      return `${who} withdrew an invitation`;
    case 'gang.invite.decline':
      return `${who} turned down an invitation`;
    case 'gang.invite.expire':
      return `An invitation to ${e.targetName ?? 'a recruit'} ran out`;
    case 'gang.invite.void':
      return `${who} joined another gang; an invitation was voided`;
    case 'gang.join':
      return `${who} joined the gang`;
    case 'gang.leave':
      return `${who} left the gang`;
    case 'gang.kick':
      return `${who} kicked ${target} out`;
    case 'gang.role.set': {
      const verb = d.kind === 'demote' ? 'demoted' : 'promoted';
      return `${who} ${verb} ${target} from ${str(d.from, '?')} to ${str(d.to, '?')}`;
    }
    case 'gang.role.create':
      return `${who} created the role ${str(d.name, '?')}`;
    case 'gang.role.edit':
      return `${who} edited the role ${str(d.name, '?')}`;
    case 'gang.role.delete':
      return `${who} deleted the role ${str(d.name, '?')}`;
    case 'gang.leader.transfer':
      return `${who} handed the gang to ${target}`;
    case 'gang.leader.auto':
      return `${str(d.toName, 'The most senior member')} took over as boss (${str(d.reason, 'the boss left')})`;
    case 'gang.disband':
      return `${who} disbanded the gang${typeof d.refunded === 'number' && d.refunded > 0 ? `, ${money(d.refunded)} returned` : ''}`;
    case 'gang.treasury.deposit':
      return `${who} put ${pounds(d.amount)} in the treasury`;
    case 'gang.treasury.withdraw':
      return `${who} took ${pounds(d.amount)} from the treasury`;
    case 'gang.treasury.spend':
      return `${who} spent ${pounds(d.amount)} from the treasury${d.what ? ` on ${str(d.what)}` : ''}`;
    case 'gang.treasury.income':
      return `${pounds(d.amount)} came into the treasury${d.source ? ` (${str(d.source)})` : ''}`;
    case 'gang.stash.in':
    case 'gang.stash.out': {
      const def = str(d.def);
      const item = def ? (getItemDef(def)?.name ?? def) : 'goods';
      const qty = typeof d.qty === 'number' ? `${d.qty} × ` : '';
      return e.action === 'gang.stash.in' ? `${who} stashed ${qty}${item}` : `${who} took ${qty}${item} from the stash`;
    }
    case 'gang.style':
      return `${who} changed the gang colours`;
    case 'gang.progress': {
      const gained = typeof d.reputation === 'number' ? d.reputation : 0;
      return `The gang earned ${gained} reputation${d.tierUp ? ' and moved up a tier' : ''}`;
    }
    case 'gang.denied':
      return `${who} tried to ${DENIED_WORDS[str(d.action)] ?? str(d.action, 'do something')} and was refused${d.reason ? ` (${str(d.reason)})` : ''}`;
    default:
      return e.action.startsWith('gang.business.') ? businessSentence(e.action.slice('gang.business.'.length), who, d) : `${who}: ${e.action}`;
  }
}

/** Refused property attempts (`gang.denied` {action}) in words. */
const DENIED_WORDS: Record<string, string> = {
  'property.takings': 'sell takings',
  'property.collect': 'box up stock',
  'property.supplies': 'order supplies',
  'property.upgrade': 'buy an improvement',
  'property.install': 'fit an operation',
  'property.pay': 'pay the bills',
  'property.buy': 'buy premises for the gang',
  'property.settings': 'change the members’ cut',
  'property.request': 'move premises',
  'property.reply': 'answer a request about premises',
};

/** A gang business line (`gang.business.<what>` audit rows, docs/PROPERTIES.md §15). */
function businessSentence(what: string, who: string, d: Record<string, unknown>): string {
  const site = siteName(d.site);
  const goods = (n: unknown, def: unknown): string => {
    const id = str(def);
    const it = id ? getItemDef(id) : undefined;
    const qty = typeof n === 'number' ? n : 0;
    return `${qty} ${it ? (qty === 1 ? it.name : it.plural) : 'units'}`;
  };
  switch (what) {
    case 'buy':
      return `${who} bought ${site} for the gang (${pounds(d.price)} from the treasury)`;
    case 'install':
      return `${who} fitted an operation at ${site} (${pounds(d.price)})`;
    case 'supplies':
      return `${who} ordered ${typeof d.qty === 'number' ? d.qty : '?'} supplies for ${site} (${pounds(d.total)})`;
    case 'upgrade':
      return `${who} improved ${site}: ${str(d.track, 'a track')} level ${typeof d.level === 'number' ? d.level : '?'} (${pounds(d.cost)})`;
    case 'collect':
      return `${who} boxed up ${goods(d.qty, d.def)} at ${site}${d.to === 'self' ? ' into their pockets' : d.to === 'veh' ? ' into a boot' : ''}`;
    case 'takings':
      return `${who} sold ${goods(d.qty, d.def)} from ${site} to the wholesaler: ${pounds(d.treasury)} to the treasury${typeof d.cut === 'number' && d.cut > 0 ? `, ${pounds(d.cut)} cut` : ''}`;
    case 'pay':
      return `${who} paid the running costs at ${site} (${pounds(d.amount)})`;
    case 'store':
      return `${who} ${d.dir === 'in' ? 'put' : 'took'} ${goods(d.qty, d.def)} ${d.dir === 'in' ? 'into' : 'out of'} the store at ${site}`;
    case 'settings':
      return `${who} set the members’ cut on takings to ${typeof d.cut === 'number' ? d.cut : 0} %`;
    case 'access':
      return `${who} ${d.allow ? 'opened' : 'closed'} their ${site} to the gang`;
    case 'request':
      return d.dir === 'in' ? `${who} offered ${site} to the gang` : `${who} asked to buy ${site} out (${pounds(d.price)})`;
    case 'request_cancelled':
      return `${who} withdrew a request about premises`;
    case 'request_declined':
      return `${who} turned down a request about premises`;
    case 'request_expired':
    case 'request_void':
      return 'A request about premises lapsed';
    case 'in':
      return `${who} took ${site} into the gang`;
    case 'out':
      return `${who} let ${site} go to a member for ${pounds(d.price)}`;
    case 'raid_warning':
      return `Word came that the police are coming to ${site}`;
    case 'raid': {
      const n = Array.isArray(d.defenders) ? d.defenders.length : 0;
      return d.outcome === 'defended'
        ? `The police came to ${site} and were seen off (${n} at the door)`
        : d.outcome === 'seized'
          ? `The police raided ${site} and took ${typeof d.units === 'number' ? d.units : '?'} units`
          : `The police raided ${site} and found nothing`;
    }
    case 'power_cut':
      return `The power went off at ${site}: the treasury could not pay`;
    case 'repossessed':
      return 'The landlord took back premises for unpaid rent';
    case 'settle':
      return d.how === 'handover' ? `${site} went to ${who} when the gang broke up` : `${site} went back to the landlord when the gang broke up`;
    default:
      return `${who}: business ${what}`;
  }
}

function siteName(id: unknown): string {
  const s = typeof id === 'string' ? LOCKUPS.find((l) => l.id === id) : undefined;
  return s?.name ?? 'the premises';
}
